import { useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckSquare, X } from 'lucide-react';
import { useDismissedUIElements } from '@/hooks/useDismissedUIElements';

interface AnchorMilestonesNoticeProps {
  jobId: string;
}

export function AnchorMilestonesNotice({ jobId }: AnchorMilestonesNoticeProps) {
  const { isDismissed, isLoading, dismiss, isDismissing } = useDismissedUIElements(
    'anchor_milestones_notice',
    { job_id: jobId }
  );

  // Auto-mark as shown when component mounts and is about to be displayed
  useEffect(() => {
    if (!isDismissed && !isLoading && !isDismissing) {
      dismiss();
    }
  }, [isDismissed, isLoading, isDismissing, dismiss]);

  // Don't render if dismissed or still loading
  if (isDismissed || isLoading) {
    return null;
  }

  return (
    <Card className="bg-blue-50 border-blue-200">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-blue-100 p-2">
            <CheckSquare className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 mb-1">Anchor Milestones Created</h3>
            <p className="text-sm text-blue-800">
              Two key milestones have been automatically created from your job dates: <strong>Art Files Ready for Production</strong> (client responsibility) and <strong>Final Delivery Due</strong> (vendor responsibility). You can add additional milestones between these bookends and edit any milestone dates during the planning phase.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => dismiss()}
            disabled={isDismissing}
            className="text-blue-700 hover:text-blue-900 hover:bg-blue-100 h-8 w-8 p-0 shrink-0"
            aria-label="Dismiss notice"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
